import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HARDCODED_BANKS } from '../../constants';
import { useInstitutions } from '../../hooks/useInstitutions';
import { Institution } from '../../types/institution';
import SearchResults from '../common/SearchResults';
import FinicityConnectSDK from '../finicity/FinicityConnectSDK';
import AccountSelectionStep from '../common/AccountSelectionStep';
import { useToast } from '../../contexts/ToastContext';
import { FinicityConnectionResult } from '../../services/finicityService';
import { institutionService, userService, ApiClientError } from '../../services/api';
import { track, EVENTS } from '../../analytics';
import { getAuthToken, getCurrentUserId, getFirebaseAuthFromIndexedDB } from '../../services/auth/authService';
import checkmarkIcon from '../../assets/icons/checkmark-icon.svg';
import './BankAccountStep.css';

export interface BankAccountStepProps {
  onNext: () => void;
  onPrev: () => void;
}

// Sub-step types for the bank account flow
type BankAccountSubStep = 'bank-selection' | 'finicity-connect' | 'account-selection';

// Display banks for the grid
const DISPLAY_BANKS = [
  {
    id: "102224",
    name: "Chase",
    icon: "https://prod-carpintero-branding.s3.us-west-2.amazonaws.com/102224/icon.svg",
  },
  {
    id: "170758",
    name: "Bank of America",
    icon: "https://prod-carpintero-branding.s3.us-west-2.amazonaws.com/170758/icon.svg",
  },
  {
    id: "170718",
    name: "Wells Fargo",
    icon: "https://prod-carpintero-branding.s3.us-west-2.amazonaws.com/170718/icon.svg",
  },
  {
    id: "1003",
    name: "Citigroup",
    icon: "https://prod-carpintero-branding.s3.us-west-2.amazonaws.com/1003/icon.svg",
  },
  {
    id: "170778",
    name: "Capital One",
    icon: "https://prod-carpintero-branding.s3.us-west-2.amazonaws.com/170778/icon.svg",
  },
  {
    id: "170894",
    name: "US Bank",
    icon: "https://prod-carpintero-branding.s3.us-west-2.amazonaws.com/170894/icon.svg",
  },
  {
    id: "176917",
    name: "PNC",
    icon: "https://prod-carpintero-branding.s3.us-west-2.amazonaws.com/176917/icon.svg",
  },
  {
    id: "171292",
    name: "Truist",
    icon: "https://prod-carpintero-branding.s3.us-west-2.amazonaws.com/171292/icon.svg",
  },
  {
    id: "170935",
    name: "Chime",
    icon: "https://prod-carpintero-branding.s3.us-west-2.amazonaws.com/170935/icon.svg",
  },
];

const BankAccountStep: React.FC<BankAccountStepProps> = ({ onNext, onPrev }) => {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBank, setSelectedBank] = useState<string | null>(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [currentSubStep, setCurrentSubStep] = useState<BankAccountSubStep>('bank-selection');
  const [selectedInstitution, setSelectedInstitution] = useState<Institution | null>(null);
  const [connectionResult, setConnectionResult] = useState<FinicityConnectionResult | null>(null);
  const [showChimeDialog, setShowChimeDialog] = useState(false);
  const [isLoadingChimeConfirmation, setIsLoadingChimeConfirmation] = useState(false);
  const [userData, setUserData] = useState<any>(null); // TEMP: Store user data
  const [hasRedirected, setHasRedirected] = useState(false); // Track if we've redirected from Finicity
  const [isProcessingBank, setIsProcessingBank] = useState(false); // Track if bank connection is processing
  const { showToast } = useToast();

  // Helper function to get user-friendly error messages
  const getErrorMessage = (error: unknown, defaultMessage: string): string => {
    if (error instanceof ApiClientError) {
      // Check if it's a network error (status 0) or generic "Failed to fetch"
      if (error.status === 0 || error.message.toLowerCase().includes('failed to fetch')) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if (error.status === 401 || error.status === 403) {
        return 'Authentication failed. Please refresh the page and try again.';
      } else if (error.status >= 500) {
        return 'Server error. Please try again in a few moments.';
      } else {
        return error.message || defaultMessage;
      }
    } else if (error instanceof Error) {
      // Handle generic errors
      if (error.message.toLowerCase().includes('failed to fetch') || error.message.toLowerCase().includes('network')) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      } else {
        return error.message || defaultMessage;
      }
    }
    return defaultMessage;
  };

  // Use the institutions hook for API search
  const { institutions, loading, error: institutionsError, searchInstitutions } = useInstitutions();

  // Detect redirect from Finicity when component loads
  useEffect(() => {
    const reason = searchParams.get('reason');
    const code = searchParams.get('code');

    if (reason || code) {
      console.log('Finicity redirect detected:', { reason, code });
      setHasRedirected(true);

      // Handle failure cases
      if (code && code !== '200') {
        console.error('Finicity connection failed:', { reason, code });
        const errorMessage = `We were unable to link your account. Please try again.`;
        track(EVENTS.errorPageView, {
          errorMessage
        });
        setIsProcessingBank(false);
        showToast({
          title: 'Something went wrong',
          message: errorMessage
        });
        // Show error and stay on bank selection
        setCurrentSubStep('bank-selection');
        // Clear any stored data
        localStorage.removeItem('finicity_institution_id');
        localStorage.removeItem('finicity_institution_name');
        localStorage.removeItem('finicity_user_id');
        
        // Clear URL parameters
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      else{
        console.log('got back');
       // setIsProcessingBank(false);
      }

      // Success case - switch to account selection
      if (code === '200' || reason === 'exit') {
        console.log('Finicity connection successful, switching to account selection');
        
        // Retrieve stored data from localStorage
        const storedInstitutionId = localStorage.getItem('finicity_institution_id');
        const storedInstitutionName = localStorage.getItem('finicity_institution_name');
        const storedUserId = localStorage.getItem('finicity_user_id');

        if (storedInstitutionId) {
          // Create institution object from stored data
          const institution: Institution = {
            id: storedInstitutionId,
            name: storedInstitutionName || 'Bank',
            status: 'online',
            currency: 'USD',
            oauth_enabled: false
          };

          setSelectedInstitution(institution);
          setSelectedBank(storedInstitutionId);
          setCurrentSubStep('account-selection');

          // Clear URL parameters to prevent re-triggering
          window.history.replaceState({}, '', window.location.pathname);
        } else {
          console.error('Missing stored data after Finicity redirect');
          setCurrentSubStep('bank-selection');
        }
      }
    }
  }, [searchParams]);

 
  const logAuthToken = async () => {
    try {
      const token = await getAuthToken();
      const userId = getCurrentUserId();
      const indexedDBData = await getFirebaseAuthFromIndexedDB();
      
      console.log('🔐 Anonymous Auth Token:', token);
      console.log('🔐 Token length:', token?.length || 0);
      console.log('👤 Current User ID:', userId);
      console.log('💾 IndexedDB Auth Data:', indexedDBData);
      
      return token;
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
  };

  // Track page view when component mounts (wait for analytics to be ready)
  useEffect(() => {
    const trackEvent = async () => {
      await track(EVENTS.bankSelectionPageView);
      logAuthToken(); // Log the anonymous auth token
    };
    trackEvent();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim().length > 2) { // Only search after 3+ characters
        setShowSearchResults(true);
        // Track search initiated event
        track(EVENTS.searchInitiated, { searchQuery: searchTerm.trim() });
        searchInstitutions(searchTerm);
      } else {
        setShowSearchResults(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchInstitutions]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleBankSelect = async (bankId: string) => {
    setSelectedBank(bankId);

    // Check if Chime is selected - show confirmation dialog
    if (bankId === "170935") {
      setShowChimeDialog(true);
      return;
    }
    
    // Find the selected bank from display banks first, then fallback to hardcoded banks
    const displayBank = DISPLAY_BANKS.find(b => b.id === bankId);
    const bank = HARDCODED_BANKS.find(b => b.id === bankId);
    
    if (displayBank || bank) {
      // Immediately dismiss bank list and show processing
      setShowSearchResults(false);
      setIsProcessingBank(true);

      // Track top bank selected event
      track(EVENTS.topBankSelected, {
        selectedBank: displayBank?.name || bank?.name || '',
        institutionId: bankId
      });

      // Create user if not already created (only once when bank is selected)
      if (!userData) {
        try {
          console.log('Creating user...');
          const newUser = await userService.createUser();
          console.log('✅ User created:', newUser);
          setUserData(newUser);
        } catch (error) {
          console.error('❌ Failed to create user:', error);
          // Continue with bank selection even if user creation fails
        }
      }

      // Convert to Institution format
      const institution: Institution = {
        id: bankId,
        name: displayBank?.name || bank?.name || '',
        home_url: bank?.home_url || '',
        login_url: bank?.login_url || '',
        forgot_password_url: bank?.forgot_password_url || '',
        signup_url: bank?.signup_url || '',
        supports_transaction_aggregation: bank?.supports_transaction_aggregation || false,
        supports_account_verification: bank?.supports_account_verification || false,
        supports_account_history: bank?.supports_account_history || false,
        supports_account_owner_info: bank?.supports_account_owner_info || false,
        supports_student_loan_data: bank?.supports_student_loan_data || false,
        supports_loan_payment_details: bank?.supports_loan_payment_details || false,
        account_type: bank?.account_type || '',
        institution_type: bank?.institution_type || '',
        status: bank?.status || 'online',
        time_zone: bank?.time_zone || '',
        currency: bank?.currency || 'USD',
        phone: bank?.phone || '',
        email: bank?.email || '',
        address: {
          address_line1: bank?.address?.address_line1 || '',
          address_line2: bank?.address?.address_line2 || '',
          city: bank?.address?.city || '',
          state: bank?.address?.state || '',
          postal_code: bank?.address?.postal_code || '',
          country: bank?.address?.country || 'USA',
        },
        branding: {
          logo: displayBank?.icon || bank?.branding?.logo || '',
          alternate_logo: bank?.branding?.alternate_logo || '',
          icon: displayBank?.icon || bank?.branding?.icon || '',
          primary_color: bank?.branding?.primary_color || '',
          tile: bank?.branding?.tile || '',
        },
        oauth_enabled: bank?.oauthEnabled || false,
        special_instructions: bank?.special_instructions || [],
        special_text: bank?.special_text || '',
      };

      setSelectedInstitution(institution);
      setCurrentSubStep('finicity-connect');
    }
  };

  const handleInstitutionSelect = async (institution: Institution) => {
    // Immediately dismiss search results and show processing
    setShowSearchResults(false);
    setIsProcessingBank(true);

    // Track search results bank selected event
    track(EVENTS.searchResultsBankSelected, {
      bankName: institution.name,
      institutionId: institution.id
    });
    
    // Create user if not already created (only once when bank is selected)
    if (!userData) {
      try {
        console.log('Creating user...');
        const newUser = await userService.createUser();
        console.log('✅ User created:', newUser);
        setUserData(newUser);
      } catch (error) {
        console.error('❌ Failed to create user:', error);
        // Continue with bank selection even if user creation fails
      }
    }
    
    setSelectedBank(institution.id);
    setSelectedInstitution(institution);
    setCurrentSubStep('finicity-connect');
  };

  const handleFinicitySuccess = (result: FinicityConnectionResult) => {
    console.log('Finicity connection successful:', result);
    setIsProcessingBank(false);
    setConnectionResult(result);
    setCurrentSubStep('account-selection');
  };

  const handleFinicityCancel = () => {
    console.log('Finicity connection cancelled');
    setIsProcessingBank(false);
    setCurrentSubStep('bank-selection');
    setSelectedBank(null);
    setSelectedInstitution(null);
  };

  const handleFinicityError = (errorMessage: string) => {
    console.error('Finicity connection error:', errorMessage);
    setIsProcessingBank(false);
    showToast({
      title: 'Something went wrong',
      message: errorMessage
    });
    setCurrentSubStep('bank-selection');
    setSelectedBank(null);
    setSelectedInstitution(null);
  };

  const handleAccountSelectionNext = () => {
    console.log('Account selection completed');
    // Proceed to verification step
    onNext();
  };

  const handleAccountSelectionPrev = () => {
    console.log('Going back from account selection');
    setCurrentSubStep('bank-selection');
    setSelectedBank(null);
    setSelectedInstitution(null);
    setConnectionResult(null);
  };


  const handleChimePrimaryAccount = async () => {
    try {
      setIsLoadingChimeConfirmation(true);
      console.log('Fetching institutions data for Chime primary account...');
      
      // Track Chime primary bank agreed event
      track(EVENTS.chimeIsPrimaryBankAgreed);
      
      // Call institutions API
      const institutions = await institutionService.getInstitutions();
      console.log('Institutions fetched successfully:', institutions.length, 'institutions');
      
      setIsLoadingChimeConfirmation(false);
      setShowChimeDialog(false);
      
      // Proceed with Chime connection
      handleBankSelectContinue("170935");
    } catch (error) {
      console.error('Failed to fetch institutions:', error);
      const errorMessage = getErrorMessage(error, 'Failed to fetch institutions. Please try again.');
      showToast({
        title: 'Something went wrong',
        message: errorMessage,
        autoClose: false
      });
      setIsLoadingChimeConfirmation(false);
      setShowChimeDialog(false);
      
      // Still proceed with Chime connection even if API call fails
      handleBankSelectContinue("170935");
    }
  };

  const handleChimeSelectDifferentBank = () => {
    // Track Chime primary bank disagreed event
    track(EVENTS.chimeIsPrimaryBankDisagreed);
    setShowChimeDialog(false);
    setSelectedBank(null);
  };

  const handleBankSelectContinue = async (bankId: string) => {
    const displayBank = DISPLAY_BANKS.find(b => b.id === bankId);
    const bank = HARDCODED_BANKS.find(b => b.id === bankId);
    
    if (displayBank || bank) {
      // Create user if not already created (only once when bank is selected)
      if (!userData) {
        try {
          console.log('Creating user...');
          const newUser = await userService.createUser();
          console.log('✅ User created:', newUser);
          setUserData(newUser);
        } catch (error) {
          console.error('❌ Failed to create user:', error);
          // Continue with bank selection even if user creation fails
        }
      }

      const institution: Institution = {
        id: bankId,
        name: displayBank?.name || bank?.name || '',
        home_url: bank?.home_url || '',
        login_url: bank?.login_url || '',
        forgot_password_url: bank?.forgot_password_url || '',
        signup_url: bank?.signup_url || '',
        supports_transaction_aggregation: bank?.supports_transaction_aggregation || false,
        supports_account_verification: bank?.supports_account_verification || false,
        supports_account_history: bank?.supports_account_history || false,
        supports_account_owner_info: bank?.supports_account_owner_info || false,
        supports_student_loan_data: bank?.supports_student_loan_data || false,
        supports_loan_payment_details: bank?.supports_loan_payment_details || false,
        account_type: bank?.account_type || '',
        institution_type: bank?.institution_type || '',
        status: bank?.status || 'online',
        time_zone: bank?.time_zone || '',
        currency: bank?.currency || 'USD',
        phone: bank?.phone || '',
        email: bank?.email || '',
        address: {
          address_line1: bank?.address?.address_line1 || '',
          address_line2: bank?.address?.address_line2 || '',
          city: bank?.address?.city || '',
          state: bank?.address?.state || '',
          postal_code: bank?.address?.postal_code || '',
          country: bank?.address?.country || 'USA',
        },
        branding: {
          logo: displayBank?.icon || bank?.branding?.logo || '',
          alternate_logo: bank?.branding?.alternate_logo || '',
          icon: displayBank?.icon || bank?.branding?.icon || '',
          primary_color: bank?.branding?.primary_color || '',
          tile: bank?.branding?.tile || '',
        },
        oauth_enabled: bank?.oauthEnabled || false,
        special_instructions: bank?.special_instructions || [],
        special_text: bank?.special_text || '',
      };

      setSelectedInstitution(institution);
      setCurrentSubStep('finicity-connect');
    }
  };

  // Filter display banks only when not showing search results
  const filteredBanks = showSearchResults ? [] : DISPLAY_BANKS.filter(bank =>
    searchTerm.trim().length === 0 || // Show all banks when no search term
    bank.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Render different sub-steps
  const renderSubStep = () => {
    switch (currentSubStep) {
      case 'finicity-connect':
        if (selectedInstitution) {
          return (
            <div className="finicity-connect-substep">
              <FinicityConnectSDK
                institution={selectedInstitution}
                onSuccess={handleFinicitySuccess}
                onCancel={handleFinicityCancel}
                onError={handleFinicityError}
                userData={userData}
              />
            </div>
          );
        }
        return null;

      case 'account-selection':
        // Only show AccountSelectionStep if we have redirected from Finicity
        if (selectedInstitution && hasRedirected) {
          return (
            <div className="account-selection-substep">
              <AccountSelectionStep
                institution={selectedInstitution}
                onNext={handleAccountSelectionNext}
                onPrev={handleAccountSelectionPrev}
              />
            </div>
          );
        }
        // If no redirect, go back to bank selection
        if (!hasRedirected && currentSubStep === 'account-selection') {
          setCurrentSubStep('bank-selection');
        }
        return null;

      case 'bank-selection':
      default:
        return (
          <div className="bank-selection-substep">
            <div className="bank-account-container">
        
              {/* Right Side - Bank Selection */}
              <div className="bank-selection-section">
                <h1 className="bank-selection-heading">
                Link a checking account (at least $500 in monthly debit card spends) to get Dayli card 
                </h1>

                {/* Search Bar */}
                <div className="search-container">
                  <div className="search-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle cx="11" cy="11" r="8" stroke="#BABABA" strokeWidth="2" />
                      <path d="m21 21-4.35-4.35" stroke="#BABABA" strokeWidth="2" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search bank"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>

                {/* Security Message */}
                <div className="security-message-badge">
                  <div className="security-message-content">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1.31L2 3.65V7.31C2 11.31 4.67 14.81 8 15.65C11.33 14.81 14 11.31 14 7.31V3.65L8 1.31Z" fill="#F65E18"/>
                      <path d="M8 5.5C7.45 5.5 7 5.95 7 6.5V8.5C7 9.05 7.45 9.5 8 9.5C8.55 9.5 9 9.05 9 8.5V6.5C9 5.95 8.55 5.5 8 5.5ZM8 10.5C7.45 10.5 7 10.95 7 11.5C7 12.05 7.45 12.5 8 12.5C8.55 12.5 9 12.05 9 11.5C9 10.95 8.55 10.5 8 10.5Z" fill="white"/>
                    </svg>
                    <span className="security-message-text">Bank-level encryption. You stay in full control.</span>
                  </div>
                </div>

                {/* Scrollable Content Area */}
                {isProcessingBank ? (
                  <div className="processing-message-container">
                    <div className="processing-spinner"></div>
                    <span className="processing-text">Processing request...</span>
                  </div>
                ) : showSearchResults ? (
                  <div className="search-results-container">
                    <SearchResults
                      searchResults={institutions}
                      isSearching={loading}
                      searchTerm={searchTerm}
                      hasSearchResults={true}
                      onBankSelect={handleInstitutionSelect}
                    />
                  </div>
                ) : (
                  <div className="bank-grid">
                    <div className="bank-row">
                      {filteredBanks.slice(0, 3).map((bank) => (
                        <div
                          key={bank.id}
                          className={`bank-item ${selectedBank === bank.id ? 'selected' : ''}`}
                          onClick={() => handleBankSelect(bank.id)}
                        >
                          <div className="bank-icon">
                            <img src={bank.icon} alt={`${bank.name} icon`} />
                          </div>
                          <span className="bank-name">{bank.name}</span>
                        </div>
                      ))}
                    </div>
                    <div className="bank-row">
                      {filteredBanks.slice(3, 6).map((bank) => (
                        <div
                          key={bank.id}
                          className={`bank-item ${selectedBank === bank.id ? 'selected' : ''}`}
                          onClick={() => handleBankSelect(bank.id)}
                        >
                          <div className="bank-icon">
                            <img src={bank.icon} alt={`${bank.name} icon`} />
                          </div>
                          <span className="bank-name">{bank.name}</span>
                        </div>
                      ))}
                    </div>
                    <div className="bank-row">
                      {filteredBanks.slice(6, 9).map((bank) => (
                        <div
                          key={bank.id}
                          className={`bank-item ${selectedBank === bank.id ? 'selected' : ''}`}
                          onClick={() => handleBankSelect(bank.id)}
                        >
                          <div className="bank-icon">
                            <img src={bank.icon} alt={`${bank.name} icon`} />
                          </div>
                          <span className="bank-name">{bank.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="terms-of-service-text">
                  By proceeding, you agree to our{' '}
                  <a href="https://dayli.cards/terms-of-use" target="_blank" rel="noopener noreferrer" className="terms-link">
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a href="https://dayli.cards/privacy-policy" target="_blank" rel="noopener noreferrer" className="terms-link">
                    Privacy Policy
                  </a>
                  .
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  // Show Toast for errors
  useEffect(() => {
    if (institutionsError) {
      showToast({
        title: 'Something went wrong',
        message: institutionsError
      });
    }
  }, [institutionsError, showToast]);

  return (
    <div className="bank-account-step">
      {renderSubStep()}

      {/* Chime Confirmation Dialog */}
      {showChimeDialog && (
        <div className="chime-dialog-overlay" onClick={handleChimeSelectDifferentBank}>
          <div className="chime-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="chime-dialog-content">
              <div className="chime-dialog-header">
                <img 
                  src="https://prod-carpintero-branding.s3.us-west-2.amazonaws.com/170935/icon.svg" 
                  alt="Chime" 
                  className="chime-logo"
                />
                <div className="chime-dialog-text">
                  <h2 className="chime-dialog-title">Is Chime your primary bank account?</h2>
                  <p className="chime-dialog-subtitle">Dayli requires at least $500 monthly debit card spends</p>
                </div>
              </div>
              <div className="chime-dialog-actions">
                <button 
                  className="chime-dialog-button primary"
                  onClick={handleChimePrimaryAccount}
                  disabled={isLoadingChimeConfirmation}
                >
                  {isLoadingChimeConfirmation ? 'Loading...' : 'Yes, this is my primary account'}
                </button>
                <button 
                  className="chime-dialog-button secondary"
                  onClick={handleChimeSelectDifferentBank}
                  disabled={isLoadingChimeConfirmation}
                >
                  Select different Bank
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankAccountStep; 